import { useState } from 'react';
import type { FormEvent } from 'react';
import { isNil } from 'lodash';

import { Alert, Button } from '~/components/ui';
import { TextField } from '~/components/form';
import globalState, { useAppDispatch } from '~/state/global';
import { queryErrorMessage } from '~/modules/query/base';
import { useLoginMutation } from '~/modules/query/auth';

export const Login = () => {
  const globalDispatch = useAppDispatch();

  const [loginInfo, setLoginInfo] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState<string>();
  const [login, { isLoading: isProcessing }] = useLoginMutation();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    handleLogin();
  };

  const handleLogin = async () => {
    try {
      const resp = await login(loginInfo).unwrap();

      if (resp.status === 0 && 'result' in resp) {
        globalDispatch(globalState.setUserState(resp.result.session?.user));
        globalDispatch(globalState.setActingAsState(resp.result?.session?.user));
        setLoginError(undefined);
      } else {
        setLoginError(resp.message);
      }
    } catch (error) {
      setLoginError(queryErrorMessage(error) ?? 'Unable to sign in. Please try again.');
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 font-outfit lg:grid-cols-3">
      <div className="relative hidden lg:col-span-2 lg:block">
        <img className="h-full w-full object-cover" src="/assets/img/login-background.jpg" alt="" />
      </div>
      <div className="flex items-center justify-center bg-white px-6 py-12 text-gray-800 dark:bg-gray-900 dark:text-white/90">
        <form id="loginForm" onSubmit={handleSubmit} className="w-full max-w-sm space-y-6">
          <img className="mx-auto w-40" src="/assets/img/toolbox-logo.png" alt="Toolbox" />

          {!isNil(loginError) && (
            <Alert variant="error">
              <p className="text-sm text-gray-800 dark:text-white/90">{loginError}</p>
            </Alert>
          )}

          <TextField
            id="loginForm-email"
            testId="login-email-input"
            name="email"
            type="email"
            label="Email"
            placeholder="Enter email"
            autoComplete="email"
            required
            value={loginInfo.email}
            onChange={(email) => setLoginInfo((prev) => ({ ...prev, email }))}
          />

          <TextField
            id="loginForm-password"
            testId="login-password-input"
            name="password"
            type="password"
            label="Password"
            placeholder="Password"
            autoComplete="current-password"
            required
            value={loginInfo.password}
            onChange={(password) => setLoginInfo((prev) => ({ ...prev, password }))}
          />

          <Button
            type="submit"
            name="login"
            size="md"
            loading={isProcessing}
            className="w-full"
            testId="login-submit-button"
          >
            {isProcessing ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </div>
    </div>
  );
};
